import type { JobProgress } from "@entrilia/shared/api/types";

type TestEmailResultSeverity = "success" | "error" | "info";

export interface TestEmailCheck {
  label: string;
  value: string;
  ok?: boolean;
}

export interface TestEmailResult {
  finished: boolean;
  severity: TestEmailResultSeverity;
  summary: string;
  checks: TestEmailCheck[];
}

const outcomeSummaries: Record<string, { severity: TestEmailResultSeverity; summary: string }> = {
  Received: { severity: "success", summary: "The test email arrived in the Entrilia test inbox." },
  Delivered: { severity: "success", summary: "The test email was delivered." },
  NotReceived: { severity: "error", summary: "The test email did not arrive within two minutes." },
  NotDelivered: { severity: "error", summary: "The test email was not delivered within two minutes." },
  Bounced: { severity: "error", summary: "The test email bounced." },
  SendRefused: { severity: "error", summary: "The test email could not be sent." },
};

const sendReasonLabels: Record<string, string> = {
  AccountUnusable: "this address is not assigned to the client or cannot send",
  NoDefaultChosen: "no default address is chosen for the client",
  ClientNotFound: "the client was not found",
  LookupFailed: "the address lookup failed, try again",
  SendFailed: "Mailgun did not accept the email",
  InvalidContent: "the message content was rejected",
  InvalidReplyTo: "the reply-to address is not valid",
  InvalidRequest: "the request was rejected",
  Accepted: "a temporary sending failure; the email was queued for a retry",
};

const toVerdict = (value: string | undefined): boolean | undefined =>
  value === undefined ? undefined : value === "True";

const getAuthenticationChecks = (properties: Record<string, string>): TestEmailCheck[] => {
  const dmarcPass = toVerdict(properties["DmarcPass"]);
  const spamFlag = toVerdict(properties["SpamFlag"]);
  const spamValue = spamFlag === undefined ? "Unknown" : spamFlag ? "Flagged as spam" : "Not flagged";
  const checks: TestEmailCheck[] = [
    { label: "SPF", value: properties["SpfResult"] ?? "Unknown", ok: toVerdict(properties["SpfAligned"]) },
    { label: "DKIM", value: properties["DkimResult"] ?? "Unknown", ok: toVerdict(properties["DkimAligned"]) },
    {
      label: "DMARC alignment",
      value: dmarcPass === undefined ? "Unknown" : dmarcPass ? "Pass" : "Fail",
      ok: dmarcPass,
    },
    {
      label: "Spam",
      value: `${spamValue}, score ${properties["SpamScore"] ?? "unknown"}`,
      ok: spamFlag === undefined ? undefined : !spamFlag,
    },
  ];

  const receivedFrom = properties["ReceivedFrom"];
  if (receivedFrom) {
    checks.push({ label: "From", value: receivedFrom });
  }
  const receivedReplyTo = properties["ReceivedReplyTo"];
  if (receivedReplyTo) {
    checks.push({ label: "Reply-To", value: receivedReplyTo });
  }

  return checks;
};

export const getTestEmailResult = (progress: JobProgress): TestEmailResult => {
  const { properties } = progress;

  if (progress.errorMessage) {
    return { finished: progress.finished, severity: "error", summary: progress.errorMessage, checks: [] };
  }

  if (!progress.finished) {
    return { finished: false, severity: "info", summary: progress.currentProgressItemName ?? "Starting", checks: [] };
  }

  const outcomeName = properties["Outcome"];
  const outcome = outcomeName ? outcomeSummaries[outcomeName] : undefined;
  if (!outcome) {
    return { finished: true, severity: "error", summary: "The test did not finish.", checks: [] };
  }

  const checks: TestEmailCheck[] = [];
  const reason = properties["SendProblem"] ?? properties["SendOutcome"];
  if (outcomeName === "SendRefused" && reason) {
    checks.push({ label: "Reason", value: sendReasonLabels[reason] ?? reason, ok: false });
  }
  const deliveryStatus = properties["DeliveryStatus"];
  if (deliveryStatus) {
    checks.push({ label: "Delivery status", value: deliveryStatus });
  }
  const deliveryFailure = properties["DeliveryFailure"];
  if (deliveryFailure) {
    checks.push({ label: "Delivery failure", value: deliveryFailure, ok: false });
  }
  if (properties["ReceivedAt"]) {
    checks.push(...getAuthenticationChecks(properties));
  }

  const summary =
    outcomeName === "SendRefused" && properties["SendOutcome"] === "Accepted"
      ? "A temporary sending failure queued the email for a retry that this test cannot check."
      : outcome.summary;

  return { finished: true, severity: outcome.severity, summary, checks };
};
